import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { TrendingDown, Users, AlertTriangle, BarChart3 } from "lucide-react";

/**
 * Admin → DAU / Retention.
 * GET /admin/analytics/retention returns:
 *   daily[], current_dau, dau_7d_avg, dau_30d_avg, dau_change_7v_prev7_pct,
 *   at_risk_users[], mode_counts{}, reasons[]
 *
 * The mode_counts dict tells us WHICH mode each at-risk user last touched,
 * so the admin can spot which feature is driving churn.
 */
export default function RetentionTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setData(null);
    try {
      const r = await api.get(`/admin/analytics/retention?days=${d}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ error: true });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (data === null) return <TactLoader />;
  if (data?.error) return <Panel className="font-mono text-sm text-red-400">Failed to load retention data.</Panel>;

  const trendDown = (data.dau_change_7v_prev7_pct ?? 0) <= -5;
  const maxDau = Math.max(1, ...data.daily.map((d) => d.dau));

  return (
    <div className="space-y-6">
      <SectionTitle kicker="// admin control" testId="admin-retention-title">DAU Drop Analysis</SectionTitle>

      <div className="flex gap-1.5 flex-wrap" data-testid="admin-retention-range">
        {[7, 14, 30, 60].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => { setDays(d); load(d); }}
            data-testid={`admin-retention-range-${d}`}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border ${
              days === d
                ? "border-[#7C3AED] bg-[#7C3AED]/15 text-[#A5B4FC]"
                : "border-[#2A2A30] text-[#71717A] hover:text-white"
            }`}
          >{d}d</button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Users} label="Current DAU" value={data.current_dau} testId="admin-kpi-current-dau" />
        <Kpi icon={BarChart3} label="7-day avg" value={data.dau_7d_avg} testId="admin-kpi-7d-avg" />
        <Kpi icon={BarChart3} label={`${data.window_days}-day avg`} value={data.dau_30d_avg} testId="admin-kpi-30d-avg" />
        <Kpi
          icon={TrendingDown}
          label="7d vs prev 7d"
          value={`${data.dau_change_7v_prev7_pct > 0 ? "+" : ""}${data.dau_change_7v_prev7_pct}%`}
          accent={trendDown ? "#EF4444" : "#10B981"}
          testId="admin-kpi-change"
        />
      </div>

      {/* Sparkline */}
      <Panel data-testid="admin-retention-sparkline">
        <h3 className="font-heading text-lg uppercase mb-3">DAU — last {data.window_days} days</h3>
        <div className="flex items-end gap-[2px] h-32" data-testid="admin-retention-bars">
          {data.daily.map((d, i) => {
            const pct = Math.round((d.dau / maxDau) * 100);
            return (
              <div
                key={d.date}
                title={`${d.date}: ${d.dau}`}
                className={`flex-1 ${i >= data.daily.length - 7 ? "bg-[#7C3AED]" : "bg-[#A5B4FC]/60"} hover:opacity-80 transition-opacity`}
                style={{ height: `${Math.max(2, pct)}%` }}
              />
            );
          })}
        </div>
        <div className="flex justify-between font-mono text-[10px] text-[#52525B] mt-1">
          <span>{data.daily[0]?.date}</span>
          <span>{data.daily[data.daily.length - 1]?.date}</span>
        </div>
      </Panel>

      {/* Findings */}
      <Panel className="border-l-4 border-l-[#F59E0B] space-y-2" data-testid="admin-retention-reasons">
        <h3 className="font-heading text-lg uppercase flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#F59E0B]" /> Findings
        </h3>
        <ul className="space-y-2 text-sm text-[#A1A1AA] leading-relaxed">
          {data.reasons.map((r, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: r.replace(/\*\*(.*?)\*\*/g, "<strong class='text-white'>$1</strong>") }} />
          ))}
        </ul>
      </Panel>

      {/* Mode breakdown */}
      <Panel data-testid="admin-retention-modes">
        <h3 className="font-heading text-lg uppercase mb-3">At-risk users by last-touched mode</h3>
        {Object.keys(data.mode_counts || {}).length === 0 ? (
          <div className="font-mono text-sm text-[#71717A]">[ NO AT-RISK USERS ]</div>
        ) : (
          <ul className="space-y-1.5">
            {Object.entries(data.mode_counts).sort((a, b) => b[1] - a[1]).map(([mode, n]) => {
              const total = Object.values(data.mode_counts).reduce((s, v) => s + v, 0);
              const pct = total ? Math.round((n / total) * 100) : 0;
              return (
                <li key={mode} className="flex items-center gap-3" data-testid={`admin-retention-mode-${mode}`}>
                  <div className="w-32 shrink-0 font-mono text-xs text-[#A1A1AA] truncate">{mode.replace(/_/g, " ")}</div>
                  <div className="flex-1 h-2 bg-[#2A2A30] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#3B82F6]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-16 text-right font-mono text-xs text-white">{n} <span className="text-[#52525B]">({pct}%)</span></div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {/* At-risk users list */}
      <Panel data-testid="admin-retention-at-risk-list">
        <h3 className="font-heading text-lg uppercase mb-3">At-risk users ({data.at_risk_count})</h3>
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] mb-3">
          Active 8-21 days ago · silent in the last 7 days
        </p>
        {data.at_risk_users.length === 0 ? (
          <div className="font-mono text-sm text-[#71717A]">[ NO AT-RISK USERS — NICE ]</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-retention-at-risk-table">
              <thead className="font-mono text-[10px] uppercase tracking-widest text-[#71717A] border-b border-[#2A2A30]">
                <tr>
                  <th className="text-left py-2">Player</th>
                  <th className="text-left py-2">Platform</th>
                  <th className="text-left py-2">Last seen</th>
                  <th className="text-left py-2">Last action</th>
                </tr>
              </thead>
              <tbody>
                {data.at_risk_users.map((u) => (
                  <tr key={u.id} className="border-b border-[#2A2A30]/50" data-testid={`admin-retention-at-risk-${u.id}`}>
                    <td className="py-2 font-bold text-white">@{u.username || u.id.slice(0, 6)}</td>
                    <td className="py-2 text-[#A1A1AA]">{u.platform || "—"}</td>
                    <td className="py-2 font-mono text-xs text-[#A1A1AA]">{u.last_active_date}</td>
                    <td className="py-2 font-mono text-xs">
                      {u.last_action ? (
                        <span>
                          <span className="text-[#A5B4FC]">{u.last_action.mode.replace(/_/g, " ")}</span>
                          <span className="text-[#52525B]"> · {u.last_action.at.slice(0, 10)}</span>
                        </span>
                      ) : <span className="text-[#52525B]">no data</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "#A5B4FC", testId }) {
  return (
    <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-extrabold mt-0.5 tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}
